import React, { useEffect, useState } from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import QRCode from "qrcode";
import { format } from "date-fns";
import { IDeliveryBatch } from "../../../interfaces/delivery-company/tasks";
import { deliveryBatchToQRcodeID } from "../../../utils/delivery-tasks";
import { Workflow } from "../../../enums/orders";

const styles = StyleSheet.create({
    page: {
        padding: 2,
        fontSize: 10,
        fontFamily: "Helvetica",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "row", // Horizontal layout
        alignItems: "flex-start", // Align content to the top
    },
    qrContainer: {
        width: '40%', // QR code takes up 40% of the width
        alignItems: "center",
        justifyContent: "center",
    },
    qrCode: {
        width: 100, // Size of the QR code
        height: 100,
    },
    contentContainer: {
        width: '60%', // Remaining space for other data
        paddingLeft: 5, // Add some spacing between QR code and content
        paddingRight: 5,
        marginTop: 10
    },
    header: {
        fontSize: 10,
        fontWeight: "bold",
        marginBottom: 5,
        color: "#222",
    },
    section: {
        marginBottom: 5,
    },
    row: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 3,
    },
    cell: {
        fontSize: 8,
        color: "#333",
    },
    footer: {
        position: "absolute",
        bottom: 10,
        right: 10,
        fontSize: 8,
        color: "#555",
    },
});

export const generateQRCode = async (text: string) => {
    try {
        return await QRCode.toDataURL(text);
    } catch (err) {
        console.error(err);
        return "";
    }
};

interface TaskPrinterProps {
    orgId: string;
    task: IDeliveryBatch;
}

const TaskPage = ({ task, orgId }: TaskPrinterProps) => {
    const [qrCode, setQRCode] = useState("");

    useEffect(() => {
        generateQRCode(deliveryBatchToQRcodeID(task)).then(setQRCode);
    }, [task.id]);

    const total_packages = task.package_count;

    const districtsSet = Array.from(new Set(task.orders.map(
        order => order.workflow === Workflow.STANDARD
            ?
            order.customer_location?.area?.district
            :
            order.merchant_location?.area?.district
    )));

    return (
        <Page size={{ width: 150, height: 275 }} style={styles.page} orientation="landscape" wrap={false}>
            {/* QR Code at the top-left corner */}

            <View style={styles.qrContainer}>
                {qrCode && <Image style={styles.qrCode} src={qrCode} />}
            </View>

            {/* Other data to the right of the QR code */}
            <View style={styles.contentContainer}>
                <View style={styles.header}>
                    <Text>Delivery Batch #{task.id}</Text>
                </View>

                <View style={styles.section}>
                    <Text>{format(new Date(task.creation_date ?? ""), 'dd-MM-yyyy HH:mm')}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.cell}>Driver: {task.driver?.name}</Text>

                </View>

                <View style={styles.row}>
                    <Text style={styles.cell}>Packages: {total_packages}</Text>
                </View>
                <View style={styles.row}>
                    <Text style={styles.cell}>Districts: {districtsSet.join(", ")}</Text>
                </View>

                <Text>{orgId}</Text>
            </View>
        </Page>
    );
};

export const TaskPrinter = ({ orgId, task }: TaskPrinterProps) => {
    return (
        <Document>
            <TaskPage task={task} orgId={orgId} />
        </Document>
    );
};